import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { BASE_URL, BASE_AUTH_URL } from '../components/constants';
import { authHeader, authHeaders } from '../utils/authHeaders';

// Async thunk for fetching all users
export const fetchAllUsers = createAsyncThunk(
  'users/fetchAllUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/users`, {
        headers: { ...authHeader() },
      });
      return response.data.users;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch users');
    }
  }
);

// Async thunk for updating user role
export const updateUserRole = createAsyncThunk(
  'users/updateRole',
  async ({ userEmail, newRole }, { rejectWithValue }) => {
    try {
      await axios.put(
        `${BASE_URL}/users/role`,
        { user_email: userEmail, new_role: newRole },
        authHeaders(),
      );
      return { userEmail, newRole };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update role');
    }
  }
);

// Async thunk for deactivating a user account (blocks their login)
export const deactivateUser = createAsyncThunk(
  'users/deactivate',
  async (userEmail, { rejectWithValue }) => {
    try {
      await axios.post(
        `${BASE_AUTH_URL}/admin/deactivate-user`,
        { email: userEmail },
        authHeaders(),
      );
      return { userEmail };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to deactivate user');
    }
  }
);

// Async thunk for reactivating a previously deactivated user account
export const activateUser = createAsyncThunk(
  'users/activate',
  async (userEmail, { rejectWithValue }) => {
    try {
      await axios.post(
        `${BASE_AUTH_URL}/admin/activate-user`,
        { email: userEmail },
        authHeaders(),
      );
      return { userEmail };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to activate user');
    }
  }
);

const initialState = {
  users: [],
  status: 'idle',
  error: null,
  updateStatus: 'idle',
  updateError: null
};

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    resetUsers: (state) => {
      state.users = [];
      state.status = 'idle';
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllUsers.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.users = action.payload;
        state.error = null;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(updateUserRole.pending, (state) => {
        state.updateStatus = 'loading';
        state.updateError = null;
      })
      .addCase(updateUserRole.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        state.updateError = null;
        const { userEmail, newRole } = action.payload;
        const user = state.users.find(u => u.user_email === userEmail);
        if (user) {
          user.user_role = newRole;
        }
      })
      .addCase(updateUserRole.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.updateError = action.payload;
      })
      .addCase(deactivateUser.fulfilled, (state, action) => {
        const user = state.users.find(u => u.user_email === action.payload.userEmail);
        if (user) user.is_active = false;
      })
      .addCase(activateUser.fulfilled, (state, action) => {
        const user = state.users.find(u => u.user_email === action.payload.userEmail);
        if (user) user.is_active = true;
      });
  }
});

export const { resetUsers } = usersSlice.actions;
export const selectAllUsers = (state) => state.users.users;
export const selectUsersStatus = (state) => state.users.status;
export const selectUsersError = (state) => state.users.error;
export const selectUpdateStatus = (state) => state.users.updateStatus;
export const selectUpdateError = (state) => state.users.updateError;

export default usersSlice.reducer;
